import type { PostHog } from 'posthog-js';
import { isIgnoredErrorMessage } from '@/lib/ignoredErrors';
import { currentVisitorIsBot } from '@/lib/botDetection';
import { analyticsConsent } from '@/lib/marketingConsent';

type Props = Record<string, any>;
let client: PostHog | undefined;
let initializing: Promise<void> | undefined;
let disabled = false;
const pending: Array<(sdk: PostHog) => void> = [];
const timers = new Map<string, number>();

// Keep auth, attribution and events in order while the SDK loads. In particular,
// an anonymous pageview must precede identify, and sign-out must precede new events.
function enqueue(fn: (sdk: PostHog) => void): void {
  if (disabled) return;
  if (!client) { if (pending.length < 500) pending.push(fn); return; }
  try { fn(client); } catch { /* analytics must not interrupt the product */ }
}

export const analyticsReady = () => !!client;

export function cleanProperties(props: Props = {}): Props {
  const out: Props = {};
  for (const [key, value] of Object.entries(props)) {
    // Auth callbacks, prompts, search text and freeform errors are not analytics.
    if (/^(search|hash|query|prompt|promptText|message|errorMessage|comment|answer|access_token|refresh_token|authorization|password)$/i.test(key)) continue;
    if (typeof value === 'string' && (/url|href|referrer|landing_page/i.test(key))) {
      try { const u = new URL(value, 'https://vovy.ai'); out[key] = u.origin + u.pathname; } catch { /* invalid URL */ }
    } else if (value === null || ['number', 'boolean'].includes(typeof value)) out[key] = value;
    else if (typeof value === 'string') out[key] = value.slice(0, 512);
    else if (Array.isArray(value)) out[key] = value.slice(0, 50).filter(v => ['number', 'boolean', 'string'].includes(typeof v));
  }
  return out;
}

// One person-property vocabulary across web, desktop and server: snake_case,
// with created_at for the account's signup instant and last_seen for activity.
const PERSON_ALIASES: Record<string, string> = {
  $email: 'email', $name: 'name', $avatar: 'avatar', $created: 'created_at',
  account_created_at: 'created_at', accountCreatedAt: 'created_at', lastActiveDate: 'last_seen',
};

/** The standard name for a person property: the aliases above, else camelCase
 *  to snake_case. PostHog's own `$` names and labels with spaces pass through. */
export function personPropertyName(key: string): string {
  if (PERSON_ALIASES[key]) return PERSON_ALIASES[key];
  if (key.startsWith('$') || /\s/.test(key) || !/[a-z0-9][A-Z]/.test(key)) return key;
  return key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

function profileProperties(props: Props): Props {
  const clean = cleanProperties(props);
  const out: Props = {};
  // A key already in the standard form wins over a legacy key renamed onto it.
  for (const [key, value] of Object.entries(clean)) if (personPropertyName(key) === key) out[key] = value;
  for (const [key, value] of Object.entries(clean)) {
    const name = personPropertyName(key);
    if (name !== key && !(name in out)) out[name] = value;
  }
  return out;
}

// Query params that may carry prompts, search text or auth material.
const SENSITIVE_URL_PARAMS = ['q', 'query', 'search', 'prompt', 'text', 'message', 'code', 'token', 'access_token', 'refresh_token', 'id_token', 'state', 'email', 'password', 'key', 'secret'] as const;

const URL_WITH_QUERY = /((?:https?:)?\/\/[^\s?#"'`<>()]+|\/[^\s?#"'`<>()]*)[?#][^\s"'`<>()]*/g;
/** Removes query strings and fragments from every URL inside free text. */
export function stripUrlQueries(text: string): string {
  return text.replace(URL_WITH_QUERY, '$1');
}

/** Replay network entries keep origin + path only; never headers or bodies. */
export function maskReplayRequest<T extends { name: string; requestHeaders?: unknown; requestBody?: unknown; responseHeaders?: unknown; responseBody?: unknown }>(request: T): T {
  const name = (() => { try { const u = new URL(request.name, 'https://vovy.ai'); return u.origin + u.pathname; } catch { return ''; } })();
  return { ...request, name, requestHeaders: undefined, requestBody: undefined, responseHeaders: undefined, responseBody: undefined };
}

// Everything that renders user prompts, generated code, answers or credentials
// is blocked outright (replaced by a placeholder); all other text is masked.
export const REPLAY_BLOCK_SELECTOR = [
  '[data-ph-no-capture]', '.ph-no-capture',
  'textarea', 'input[type="password"]', '[contenteditable="true"]',
  'pre', 'code', '.monaco-editor', '.cm-editor',
  '[data-sensitive]', '[data-prompt]', '[data-user-content]',
].join(', ');

export const REPLAY_OPTIONS = {
  maskAllInputs: true,
  maskTextSelector: '*',
  blockSelector: REPLAY_BLOCK_SELECTOR,
  maskAllElementAttributes: false,
  recordHeaders: false,
  recordBody: false,
  recordCrossOriginIframes: false,
  maskCapturedNetworkRequestFn: maskReplayRequest,
} as const;

/** Exception messages and frames lose URL queries/fragments before they leave the browser. */
// True when an $exception is known third-party/browser noise (see ignoredErrors.ts).
export function isIgnoredException(props: Props): boolean {
  if (isIgnoredErrorMessage(props.$exception_message)) return true;
  const list = props.$exception_list;
  return Array.isArray(list) && list.some(item => isIgnoredErrorMessage(item?.value));
}

export function scrubException(props: Props): void {
  if (typeof props.$exception_message === 'string') props.$exception_message = stripUrlQueries(props.$exception_message).slice(0, 1000);
  const list = props.$exception_list;
  if (!Array.isArray(list)) return;
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    if (typeof item.value === 'string') item.value = stripUrlQueries(item.value).slice(0, 1000);
    const frames = item.stacktrace?.frames;
    if (!Array.isArray(frames)) continue;
    for (const frame of frames) {
      for (const key of ['filename', 'abs_path']) {
        if (frame && typeof frame[key] === 'string') frame[key] = stripUrlQueries(frame[key]);
      }
    }
  }
}

// person_profiles: 'identified_only' only holds while nothing writes a person
// for an anonymous visitor: posthog-js turns person processing ON the moment
// setPersonProperties runs, so a first-touch set_once on every landing made a
// profile for every visitor. Anonymous person writes wait here and ride the
// identify call instead (posthog-js identify takes $set and $set_once), so the
// signed-in person still gets them and an anonymous visitor never becomes one.
const deferredPerson: { set: Props; setOnce: Props } = { set: {}, setOnce: {} };

function sdkIdentified(sdk: PostHog): boolean {
  try { return sdk.get_property('$user_state') === 'identified'; } catch { return false; }
}

function writePerson(sdk: PostHog, set: Props, setOnce: Props): void {
  if (sdkIdentified(sdk)) {
    if (Object.keys(setOnce).length) sdk.setPersonProperties(set, setOnce); else sdk.setPersonProperties(set);
    return;
  }
  Object.assign(deferredPerson.set, set);
  // First write wins for set_once, as it would on the person.
  for (const [k, v] of Object.entries(setOnce)) if (!(k in deferredPerson.setOnce)) deferredPerson.setOnce[k] = v;
}

function takeDeferredPerson(): { set?: Props; setOnce?: Props } {
  const { set, setOnce } = deferredPerson;
  deferredPerson.set = {}; deferredPerson.setOnce = {};
  return { set: Object.keys(set).length ? set : undefined, setOnce: Object.keys(setOnce).length ? setOnce : undefined };
}

// Public ingestion key for project 200627 (US), public by design like the old
// Mixpanel token. Production builds fall back to it so a missing Lovable env var
// cannot silently turn web analytics off; dev builds stay dark unless configured.
const PRODUCTION_KEY = 'phc_Vcv5668YxWDy5VSJyIpPMY0UtntL06sEYLNGg2UZsaq';
// v.vovy.ai is PostHog's managed reverse proxy (CNAME in Vercel DNS), so ad
// blockers that drop *.posthog.com requests do not lose events.
const PRODUCTION_HOST = 'https://v.vovy.ai';

// ─── One stable uuid per logical event ───
// posthog-js 1.438 takes `uuid` in CaptureOptions ("useful for cross-source
// idempotency ... so storage can eventually deduplicate them"). A double
// fire of the same event with the same properties for the same person within
// the same wall-clock second (React StrictMode effects, a remounted tracker,
// a retried handler) now carries the same uuid, so PostHog's storage
// collapses it and, until it does, `count(distinct uuid)` counts it once.
// $duration is left out of the seed: a timed event that fires twice differs
// only by milliseconds there. Two genuine events a second apart, or with any
// differing property, keep distinct uuids.
function hash53(str: string, seed: number): number {
  let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507); h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507); h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/** A valid, deterministic UUID (version nibble 5, RFC variant) for a seed. */
export function stableEventUuid(seed: string): string {
  const hex = [1, 2, 3].map(n => hash53(seed, n).toString(16).padStart(14, '0')).join('').slice(0, 32).split('');
  hex[12] = '5';
  hex[16] = '89ab'[parseInt(hex[16], 16) & 3];
  const h = hex.join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value as Props).sort().map(k => `${JSON.stringify(k)}:${canonical((value as Props)[k])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
}

/** The uuid for one logical browser event: name, person, properties (minus
 *  $duration) and the second it happened in. Exported for the tests. */
export function eventUuid(event: string, distinctId: string | undefined, props: Props, timestamp: Date): string {
  const { $duration: _duration, ...rest } = props;
  return stableEventUuid(`web|${event}|${distinctId ?? ''}|${canonical(rest)}|${Math.floor(timestamp.getTime() / 1000)}`);
}

export function initializePostHog(): Promise<void> {
  if (initializing) return initializing;
  const key = (import.meta.env.VITE_POSTHOG_KEY ?? (import.meta.env.PROD ? PRODUCTION_KEY : ''))?.trim();
  const host = (import.meta.env.VITE_POSTHOG_HOST ?? (import.meta.env.PROD ? PRODUCTION_HOST : ''))?.trim();
  // A crawler, a preview fetcher or an automated browser is not a visitor:
  // no SDK, no capture, no person. The queue is dropped, not flushed.
  if (!key || !host || typeof window === 'undefined' || currentVisitorIsBot()) {
    disabled = true;
    pending.length = 0;
    return Promise.resolve();
  }
  initializing = import('posthog-js').then(({ default: sdk }) => {
    sdk.init(key, {
      api_host: host,
      // Toolbar and app links still point at PostHog itself, not the proxy.
      ui_host: 'https://us.posthog.com',
      person_profiles: 'identified_only',
      persistence: 'localStorage+cookie',
      // RouteTracker owns SPA pageviews so rerenders cannot double count them.
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: false,
      // $exception events feed PostHog error tracking (the only error event).
      capture_exceptions: { capture_unhandled_errors: true, capture_unhandled_rejections: true, capture_console_errors: false },
      // Replay is web only and production only; the project-level switch still gates it.
      disable_session_recording: !import.meta.env.PROD,
      enable_recording_console_log: true,
      // Strips #access_token style fragments from replay hrefs, and masks the
      // listed query params in replay hrefs and $current_url.
      disable_capture_url_hashes: true,
      mask_personal_data_properties: true,
      custom_personal_data_properties: [...SENSITIVE_URL_PARAMS],
      session_recording: REPLAY_OPTIONS,
      before_send: event => {
        if (event?.event === '$exception' && event.properties) {
          if (isIgnoredException(event.properties)) return null;
          scrubException(event.properties);
        }
        if (event?.properties) {
          for (const props of [event.properties, event.properties.$set, event.properties.$set_once]) {
            if (!props || typeof props !== 'object') continue;
            for (const key of ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer']) {
              const value = props[key];
              if (typeof value === 'string') {
                try { const u = new URL(value); props[key] = u.origin + u.pathname; } catch { delete props[key]; }
              }
            }
          }
        }
        return event;
      },
      loaded: loaded => {
        loaded.register({ surface: 'web', platform: 'web', analytics_schema_version: 1, environment: import.meta.env.MODE });
        loaded.register_once({ firstPageViewTime: new Date().toISOString() });
        client = loaded as PostHog;
        for (const fn of pending.splice(0)) enqueue(fn);
      },
    });
  }).catch(() => {
    initializing = undefined;
    console.warn('[posthog] SDK initialization failed');
  });
  return initializing;
}

// Website analytics and replay run only with CookieYes "analytics" consent.
// Until the visitor's choice is known the SDK is not loaded and sets no
// cookies; calls wait in the bounded queue. A later decline opts out and stops
// replay; a later accept opts back in.
export function initializePostHogWithConsent(): void {
  if (typeof document === 'undefined') return;
  const sync = () => {
    const granted = analyticsConsent();
    // Undecided visitors (never touched the banner) are tracked; only an explicit decline opts out.
    if (granted !== false) {
      if (!client) { void initializePostHog(); return; }
      if (client.has_opted_out_capturing()) { client.opt_in_capturing(); client.startSessionRecording(); }
    } else if (granted === false && client && !client.has_opted_out_capturing()) {
      client.stopSessionRecording();
      client.opt_out_capturing();
    }
  };
  document.addEventListener('cookieyes_consent_update', sync);
  document.addEventListener('cookieyes_banner_load', sync);
  sync();
}

// Compatibility facade keeps the established event taxonomy and all callers.
// There is no Mixpanel SDK or network traffic behind these methods.
export const analytics = {
  register_once: (p: Props) => enqueue(s => s.register_once(cleanProperties(p))),
  register: (p: Props) => enqueue(s => s.register(cleanProperties(p))),
  unregister: (key: string) => enqueue(s => s.unregister(key)),
  identify: (id: string) => {
    if (!id) return;
    enqueue(s => {
      const { set, setOnce } = takeDeferredPerson();
      if (set || setOnce) s.identify(id, set, setOnce); else s.identify(id);
    });
  },
  track: (event: string, props?: Props) => {
    const started = timers.get(event);
    const timestamp = new Date();
    const safeProps = cleanProperties(props);
    if (started !== undefined) { safeProps.$duration = (timestamp.getTime() - started) / 1000; timers.delete(event); }
    enqueue(s => {
      let distinctId: string | undefined;
      try { distinctId = s.get_distinct_id(); } catch { distinctId = undefined; }
      s.capture(event, safeProps, { timestamp, uuid: eventUuid(event, distinctId, safeProps, timestamp) });
    });
  },
  time_event: (event: string) => { timers.set(event, Date.now()); },
  // React error boundaries report here; extra properties go through the same scrubber.
  captureException: (error: unknown, props?: Props) => enqueue(s => { s.captureException(error, cleanProperties(props)); }),
  get_property: (key: string) => { try { return client?.get_property(key); } catch { return undefined; } },
  get_distinct_id: () => { try { return client?.get_distinct_id(); } catch { return undefined; } },
  reset: () => { timers.clear(); enqueue(s => { takeDeferredPerson(); s.reset(true); s.register({ surface: 'web', platform: 'web', analytics_schema_version: 1, environment: import.meta.env.MODE }); }); },
  people: {
    set: (p: Props) => enqueue(s => writePerson(s, profileProperties(p), {})),
    set_once: (p: Props) => enqueue(s => writePerson(s, {}, profileProperties(p))),
    // PostHog has no atomic Mixpanel people.increment equivalent. Preserve a
    // delta event; dashboards aggregate source events instead of mutable totals.
    increment: (property: string, value = 1) => {
      if (Number.isFinite(value)) enqueue(s => s.capture('profile_counter_incremented', { counter: property, increment: value }));
    },
  },
};

export default analytics;
